import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {
	json,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useRouteError,
	useNavigation,
} from "@remix-run/react";
import type {
	LinksFunction,
	LoaderFunctionArgs,
	MetaFunction,
} from "@remix-run/node";
import stylesheet from "~/tailwind.css?url";
import Header from "./components/header";

import { Toaster } from "~/components/ui/toaster";
import "~/globals.css";
import { CircleX } from "lucide-react";
import { useEffect } from "react";
import { useSetAtom } from "jotai";
import { isLoadingAtom } from "~/lib/jotai/loading";
import { allowedRegionAtom } from "~/lib/jotai/region";
import { useLoaderData } from "@remix-run/react";

export async function loader({ request }: LoaderFunctionArgs) {
	const absoluteURL = new URL(request.url).origin;
	
	return json({
		absoluteURL: process.env.PUBLIC_SITE_URL ?? absoluteURL,
		allowedRegion: process.env.ALLOWED_REGION || null,
	});
}

export const links: LinksFunction = () => [
	{ rel: "stylesheet", href: stylesheet },
	{
		rel: "stylesheet",
		href: "https://fonts.googleapis.com/css2?family=Vazirmatn:wght@100..900&display=swap",
	},
	{
		rel: "apple-touch-icon",
		sizes: "180x180",
		href: "/apple-touch-icon.png",
	},
	{
		rel: "icon",
		type: "image/png",
		sizes: "32x32",
		href: "/favicon-32x32.png",
	},
	{
		rel: "icon",
		type: "image/png",
		sizes: "16x16",
		href: "/favicon-16x16.png",
	},
	{
		rel: "icon",
		type: "image/x-icon",
		href: "/favicon.ico",
	},
	{
		rel: "manifest",
		href: "/site.webmanifest",
	},
];

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: "Claims - BCIC Online" },
		{
			name: "description",
			content:
				"When the unexpected happens, we're ready to assist. Our priority is making your claim process as easy and hassle-free as possible.",
		},

		{ property: "og:title", content: "Report a Claim | BCIC Insurance" },
		{
			property: "og:description",
			content:
				"When the unexpected happens, we're ready to assist. Our priority is making your claim process easy and hassle-free.",
		},
		{
			property: "og:image",
			content: `${data?.absoluteURL ? data.absoluteURL : ""}/og-image.png`,
		},

		{ name: "twitter:card", content: "summary" },
		{ name: "twitter:title", content: "Report a Claim | BCIC Insurance" },
		{
			name: "twitter:description",
			content:
				"When the unexpected happens, we're ready to assist. Our priority is making your claim process easy and hassle-free.",
		},
		{
			name: "twitter:image",
			content: `${data?.absoluteURL ? data.absoluteURL : ""}/og-twitter-image.png`,
		},
	];
};

export function Layout({ children }: { children: React.ReactNode }) {
	return (
		<html lang="en">
			<head>
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1"
				/>
				<Meta />
				<Links />
				<link rel="preconnect" href="https://fonts.googleapis.com" />
				<link
					rel="preconnect"
					href="https://fonts.gstatic.com"
					crossOrigin="anonymous"
				/>
			</head>
			<body className="text-companyBlack">
				<Header />
				<Toaster />
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

function App() {
	const navigation = useNavigation();
	const setIsLoading = useSetAtom(isLoadingAtom);
	const setAllowedRegion = useSetAtom(allowedRegionAtom);
	const data = useLoaderData<typeof loader>();

	useEffect(() => {
		if (navigation.state === "idle") {
			setIsLoading(false);
		}
	}, [navigation.state, setIsLoading]);

	useEffect(() => {
		// Initialize allowedRegion atom on every route
		setAllowedRegion(data.allowedRegion);
	}, [data.allowedRegion, setAllowedRegion]);

	return <Outlet />;
}

export const ErrorBoundary = () => {
	const error = useRouteError();
	captureRemixErrorBoundaryError(error);
	return (
		<div className="flex flex-col p-8 leading-[0.5rem] text-sm text-companyRed justify-center items-center">
			<CircleX className="mb-2" size={32} strokeWidth={3} />
			<p className="font-bold text-lg ">Oops!</p>
			<p>Something went wrong. Please reload and try again.</p>
		</div>
	);
};

export default withSentry(App);
